import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from "react-router-dom";

const AnimatedCounter = ({ end, duration = 2000, suffix = '', decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(decimals > 0 ? (progress * end).toFixed(decimals) : Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration, decimals]);

  return <span ref={ref}>{typeof count === 'number' ? count.toLocaleString() : count}{suffix}</span>;
};

export default function FocusDenLanding() {
  const [scrollY, setScrollY] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('premium');
  const [showPricingTooltip, setShowPricingTooltip] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    { icon: '📋', title: 'Task-Based Blocking', description: 'Create focus tasks with specific times and days. Select which apps to block for each task. Your schedule, your rules.', color: 'indigo' },
    { icon: '🚫', title: 'Smart App Blocking', description: 'Block Instagram, TikTok, YouTube, games - any app. When you try to open a blocked app, FocusDen shows a blocker screen with motivation.', color: 'red' },
    { icon: '🔕', title: 'Notification Blocker', description: 'Premium feature: Silence notifications from blocked apps during focus sessions. Zero distractions, complete focus.', color: 'yellow' },
    { icon: '⏱️', title: 'Super Focus Mode', description: 'Pomodoro timer with ambient sounds - rain, forest, café, lo-fi music. Lock your screen during sessions for ultimate focus.', color: 'teal' },
    { icon: '🔒', title: 'Strict Mode', description: "Can't resist? Enable Strict Mode with PIN, math challenge, or time delay. Even YOU can't disable it until session ends!", color: 'purple' },
    { icon: '📊', title: 'Analytics Dashboard', description: 'Track productivity score, block attempts, streaks, and time saved. See which apps distract you most and your progress over time.', color: 'teal' },
  ];

  const faqs = [
    { q: "How does FocusDen block apps?", a: "FocusDen uses Android's Accessibility Service to detect when you open a blocked app. It immediately shows a blocker screen with a motivational quote, reminding you to stay focused. The app doesn't access your personal data - it only monitors which app is in foreground." },
    { q: "What's the correct way to use FocusDen?", a: "1) Create a Task (e.g., 'Study Time' or 'Work Focus'). 2) Set the start and end time. 3) Select which days to repeat. 4) Choose distracting apps to block. 5) Save! FocusDen automatically blocks those apps during your scheduled time." },
    { q: "What is Strict Mode?", a: "Strict Mode prevents you from easily disabling app blocking. Choose a deactivation method: PIN code, time delay (wait 5-30 minutes), math challenge (solve problems), or typing challenge. Premium feature for serious focus." },
    { q: "Can I uninstall FocusDen to bypass blocking?", a: "With Strict Mode enabled, FocusDen has uninstall protection using Android's Device Admin. You'll need to complete the challenge first. This prevents impulsive uninstalls when willpower is low." },
    { q: "Does it drain battery?", a: "FocusDen is optimized for minimal battery usage (~1% per day). It only actively monitors apps during your scheduled focus times, not 24/7." },
    { q: "Is my data safe?", a: "100% private. All data stays on your device. We don't collect, store, or sell any personal information. No account required." }
  ];

  const handlePlanClick = (plan) => {
    setSelectedPlan(plan);
    const downloadSection = document.getElementById('download');
    if (downloadSection) downloadSection.scrollIntoView({ behavior: 'smooth' });
  };

  // ── Schema objects ──────────────────────────────────────────────────────────
  const softwareAppSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "FocusDen",
    "description": "The best app blocker for students. Block Instagram, YouTube and distracting apps to stay focused while studying.",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Android",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "ratingCount": "3200", "bestRating": "5", "worstRating": "1" },
    "author": {
      "@type": "Person",
      "name": "Smit Nikumbh",
      "url": "https://www.focusdenapp.com/about"
    },
    "downloadUrl": "https://play.google.com/store/apps/details?id=com.supnistic_ai.distraX",
    "screenshot": "https://www.focusdenapp.com/screenshot.png"
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "FocusDen",
    "url": "https://www.focusdenapp.com",
    "logo": "https://www.focusdenapp.com/favicon.svg",
    "description": "Your digital wellbeing companion. Block distractions, build focus, unlock your potential.",
    "founder": {
      "@type": "Person",
      "name": "Smit Nikumbh",
      "url": "https://www.focusdenapp.com/about"
    },
    "sameAs": [
      "https://twitter.com/FocusDenApp",
      "https://www.facebook.com/FocusDenApp",
      "https://www.instagram.com/focusdenapp"
    ],
    "contactPoint": { "@type": "ContactPoint", "contactType": "Customer Support", "url": "https://www.focusdenapp.com/contact" }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "How does FocusDen block apps?", "acceptedAnswer": { "@type": "Answer", "text": "FocusDen uses Android's Accessibility Service to detect when you open a blocked app. It immediately shows a blocker screen with a motivational quote. It only monitors which app is in foreground and doesn't access personal data." } },
      { "@type": "Question", "name": "What's the correct way to use FocusDen?", "acceptedAnswer": { "@type": "Answer", "text": "Create a Task, set the start and end time, select which days to repeat, choose distracting apps to block, and save. FocusDen automatically blocks those apps during your scheduled time." } },
      { "@type": "Question", "name": "What is the best app blocker for students?", "acceptedAnswer": { "@type": "Answer", "text": "FocusDen is the best app blocker for students with features like instant app blocking, focus timers, screen time analytics, and motivational quotes. Over 10000 students use it daily." } },
      { "@type": "Question", "name": "Can I block Instagram and YouTube while studying?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! FocusDen lets you block Instagram, YouTube, TikTok, and any distracting apps during study sessions. You can set custom schedules or block apps instantly with one tap." } },
      { "@type": "Question", "name": "Does it drain battery?", "acceptedAnswer": { "@type": "Answer", "text": "FocusDen is optimized for minimal battery usage (~1% per day). It only actively monitors apps during your scheduled focus times, not 24/7." } }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",     "item": "https://www.focusdenapp.com/" },
      { "@type": "ListItem", "position": 2, "name": "Features", "item": "https://www.focusdenapp.com/#features" },
      { "@type": "ListItem", "position": 3, "name": "Pricing",  "item": "https://www.focusdenapp.com/#pricing" }
    ]
  };

  return (
    <div className="font-['Outfit'] bg-[#0A0A14] text-gray-50 min-h-screen relative overflow-x-hidden">

      {/* ── SEO ── */}
      <Helmet>
        <title>FocusDen – App Blocker for Students & Study Focus</title>
        <meta name="description" content="Block Instagram, YouTube & distracting apps instantly. FocusDen helps students avoid distractions, reduce screen time by 70%, and stay focused while studying. Free download!" />
        <meta name="author" content="Smit Nikumbh" />
        <meta httpEquiv="content-language" content="en-US" />
        <link rel="canonical" href="https://www.focusdenapp.com/" />

        <meta property="og:locale"      content="en_US" />
        <meta property="og:type"        content="website" />
        <meta property="og:title"       content="Best App Blocker for Students – Block Distractions | FocusDen" />
        <meta property="og:description" content="Block Instagram, YouTube & distracting apps. Join 10,00+ students who improved focus by 3x with FocusDen." />
        <meta property="og:url"         content="https://www.focusdenapp.com/" />
        <meta property="og:site_name"   content="FocusDen" />
        <meta property="og:image"       content="https://www.focusdenapp.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt"   content="FocusDen App Blocker Interface" />

        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content="Best App Blocker for Students – Block Distractions" />
        <meta name="twitter:description" content="Block distracting apps, reduce screen time by 70%. Join 10000+ focused students." />
        <meta name="twitter:image"       content="https://www.focusdenapp.com/og-image.png" />
        <meta name="twitter:image:alt"   content="FocusDen App Screenshot" />

        <script type="application/ld+json">{JSON.stringify(softwareAppSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        html { scroll-behavior: smooth; }
        @keyframes pulse    { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.05); } }
        @keyframes gradient { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        @keyframes float    { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-15px); } }
        @keyframes shimmer  { 0% { background-position: -1000px 0; } 100% { background-position: 1000px 0; } }
        @keyframes slideUp  { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn  { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
        .animate-gradient  { background-size: 200% auto; animation: gradient 4s ease infinite; }
        .animate-float     { animation: float 4s ease-in-out infinite; }
        .animate-shimmer   { background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent); background-size: 1000px 100%; animation: shimmer 2s infinite; }
        .animate-slide-up  { animation: slideUp 0.5s ease-out forwards; }
        .animate-scale-in  { animation: scaleIn 0.3s ease-out forwards; }
      `}</style>

      {/* Background Orbs */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute w-[800px] h-[500px] -top-[10%] -left-[10%] rounded-full blur-[100px]"
          style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.3) 0%, transparent 70%)', transform: `translateY(${scrollY * 0.1}px)` }} />
        <div className="absolute w-[400px] h-[400px] top-[50%] -right-[5%] rounded-full blur-[100px]"
          style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.2) 0%, transparent 70%)', transform: `translateY(${scrollY * -0.15}px)` }} />
        <div className="absolute w-[300px] h-[300px] bottom-[10%] left-[20%] rounded-full blur-[100px]"
          style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.15) 0%, transparent 70%)', transform: `translateY(${scrollY * 0.08}px)` }} />
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-8 py-4 backdrop-blur-xl transition-all duration-300 ${scrollY > 50 ? 'bg-[#0A0A14]/95 border-b border-white/5' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt="FocusDen logo - Best app blocker for students" className="w-10 h-10 md:w-11 md:h-11" />
            <span className="text-2xl font-extrabold bg-gradient-to-r from-indigo-500 to-pink-500 bg-clip-text text-transparent">FocusDen</span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#features"    className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Features</a>
            <a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">How It Works</a>
            <a href="#science"     className="text-gray-400 hover:text-white transition-colors text-sm font-medium">The Science</a>
            <a href="#pricing"     className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Pricing</a>
            {/* ← NEW About link */}
            <Link to="/about"      className="text-gray-400 hover:text-white transition-colors text-sm font-medium">About</Link>
            <a href="#download"
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-semibold shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:scale-105 transition-all">
              Download Free
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden flex flex-col gap-1.5 p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle mobile menu">
            <span className="w-6 h-0.5 bg-white rounded transition-all"></span>
            <span className="w-6 h-0.5 bg-white rounded transition-all"></span>
            <span className="w-6 h-0.5 bg-white rounded transition-all"></span>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed top-[70px] left-0 right-0 bg-[#0A0A14] backdrop-blur-xl p-8 flex flex-col gap-6 border-t border-white/10 overflow-hidden z-40">
            <a href="#features"     onClick={() => setMobileMenuOpen(false)} className="text-gray-50 text-lg font-medium hover:bg-indigo-500/10 px-3 py-2 rounded-lg transition-colors">Features</a>
            <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-gray-50 text-lg font-medium hover:bg-indigo-500/10 px-3 py-2 rounded-lg transition-colors">How It Works</a>
            <a href="#science"      onClick={() => setMobileMenuOpen(false)} className="text-gray-50 text-lg font-medium hover:bg-indigo-500/10 px-3 py-2 rounded-lg transition-colors">The Science</a>
            <a href="#pricing"      onClick={() => setMobileMenuOpen(false)} className="text-gray-50 text-lg font-medium hover:bg-indigo-500/10 px-3 py-2 rounded-lg transition-colors">Pricing</a>
            <Link to="/about"       onClick={() => setMobileMenuOpen(false)} className="text-gray-50 text-lg font-medium hover:bg-indigo-500/10 px-3 py-2 rounded-lg transition-colors">About</Link>
            <a href="#download"     onClick={() => setMobileMenuOpen(false)} className="text-gray-50 text-lg font-medium hover:bg-indigo-500/10 px-3 py-2 rounded-lg transition-colors">Download Free</a>
          </div>
        )}
      </nav>

      {/* ==================== HERO ==================== */}
      <section className="min-h-screen flex items-center px-4 md:px-8 pt-32 md:pt-20 pb-16 relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div className="max-w-l text-center lg:text-left mx-auto lg:mx-0">
            <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-6">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              #1 App Blocker for Android
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight tracking-tight">
              FocusDen – App Blocker for Students
              <br />
              <span className="bg-gradient-to-r from-indigo-500 via-pink-500 to-amber-500 bg-clip-text text-transparent animate-gradient">
                Block Distractions & Stay Focused
              </span>
            </h1>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed">
              FocusDen helps you take control of your screen time. Create focus tasks, block distracting apps, and track your productivity. Your phone works for you, not against you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12 justify-center lg:justify-start">
              <a href="#download"
                className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-8 py-4 rounded-2xl text-lg font-semibold shadow-xl shadow-indigo-500/40 hover:scale-105 transition-transform"
                aria-label="Download FocusDen on Google Play Store">
                <span>▶</span> Get it Free on Play Store
              </a>
              <a href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white px-8 py-4 rounded-2xl text-lg font-medium hover:bg-white/10 transition-all">
                See How It Works
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-6 justify-center lg:justify-start">
              <div className="flex flex-col">
                <span className="text-3xl font-extrabold"><AnimatedCounter end={50} suffix="K+" /></span>
                <span className="text-sm text-gray-500">Downloads</span>
              </div>
              <div className="w-px h-10 bg-white/10"></div>
              <div className="flex flex-col">
                <span className="text-3xl font-extrabold"><AnimatedCounter end={4.8} decimals={1} /></span>
                <span className="text-sm text-gray-500">★ Rating</span>
              </div>
              <div className="w-px h-10 bg-white/10"></div>
              <div className="flex flex-col">
                <span className="text-3xl font-extrabold"><AnimatedCounter end={10} suffix="M+" /></span>
                <span className="text-sm text-gray-500">Hours Saved</span>
              </div>
            </div>
          </div>

          {/* Phone Mockup */}
          <div className="relative flex justify-center lg:order-last order-first">
            <div className="relative w-[240px] sm:w-[280px] h-[480px] sm:h-[560px] bg-gradient-to-b from-[#1F1F2E] to-[#16162A] rounded-[44px] p-3 shadow-[0_50px_100px_rgba(0,0,0,0.5)] ring-1 ring-white/10">
              <div className="w-full h-full bg-[#0F0F1A] rounded-[36px] p-5 overflow-hidden">
                <div className="flex justify-between items-center mb-4 pt-3">
                  <span className="font-semibold text-sm">🎯 FocusDen</span>
                  <span className="text-xs text-gray-500">Good Morning ☀️</span>
                </div>
                <div className="bg-gradient-to-br from-indigo-500/15 to-purple-600/10 border border-indigo-500/20 p-4 rounded-2xl mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-sm">📚 Study Session</span>
                    <span className="text-[10px] bg-green-500 text-white px-2 py-0.5 rounded-full">ACTIVE</span>
                  </div>
                  <div className="text-xs text-gray-400">9:00 AM - 12:00 PM</div>
                  <div className="mt-3">
                    <div className="text-[10px] text-gray-500 mb-1.5">BLOCKED APPS</div>
                    <div className="flex gap-1.5">
                      <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-xs">📷</span>
                      <span className="w-7 h-7 rounded-lg bg-black flex items-center justify-center text-xs">🎵</span>
                      <span className="w-7 h-7 rounded-lg bg-red-600 flex items-center justify-center text-xs">▶️</span>
                      <span className="w-7 h-7 rounded-lg bg-green-500 flex items-center justify-center text-xs">💬</span>
                      <span className="text-xs text-gray-500 self-center">+3</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 bg-white/5 border border-white/10 rounded-xl p-3 flex flex-col items-center gap-1">
                    <span className="text-xl">🔥</span>
                    <span className="text-[10px] text-gray-400">5 Day Streak</span>
                  </div>
                  <div className="flex-1 bg-white/5 border border-white/10 rounded-xl p-3 flex flex-col items-center gap-1">
                    <span className="text-xl">🚫</span>
                    <span className="text-[10px] text-gray-400">23 Blocked</span>
                  </div>
                </div>
                <div className="absolute bottom-5 right-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-5 py-3 rounded-full text-xs font-semibold shadow-lg shadow-indigo-500/40">
                  + Add Task
                </div>
              </div>
              <div className="absolute top-5 left-1/2 -translate-x-1/2 w-24 h-7 bg-[#0A0A14] rounded-full"></div>
            </div>
            <div className="hidden lg:block absolute top-[5%] -left-[15%] bg-[#0F0F1A]/95 backdrop-blur-xl border border-white/10 px-4 py-2.5 rounded-xl text-sm font-medium shadow-xl shadow-black/30 animate-float">🚫 Instagram Blocked</div>
            <div className="hidden lg:block absolute top-[40%] -right-[10%] bg-[#0F0F1A]/95 backdrop-blur-xl border border-white/10 px-4 py-2.5 rounded-xl text-sm font-medium shadow-xl shadow-black/30 animate-float" style={{ animationDelay: '1s' }}>⏱️ 2hr Focus Streak!</div>
            <div className="hidden lg:block absolute bottom-[15%] -left-[5%] bg-[#0F0F1A]/95 backdrop-blur-xl border border-white/10 px-4 py-2.5 rounded-xl text-sm font-medium shadow-xl shadow-black/30 animate-float" style={{ animationDelay: '2s' }}>📊 Productivity +45%</div>
          </div>
        </div>
      </section>

      {/* ==================== PROBLEM ==================== */}
      <section className="px-4 md:px-8 py-24 relative z-10" id="problem">
        <div className="max-w-6xl mx-auto">
          <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4">😰 The Problem</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-tight">Your Phone is Hijacking Your Brain</h2>
          <p className="text-lg text-gray-400 max-w-2xl leading-relaxed mb-12">
            The average person checks their phone <span className="text-amber-500 font-bold">96 times per day</span>. That's once every 10 minutes. Social media apps are designed by experts to be addictive.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: '📱', num: '4+ Hours', text: 'Daily screen time on social media', color: 'text-red-400' },
              { icon: '🧠', num: '23 Minutes', text: 'To regain focus after a distraction', color: 'text-indigo-400' },
              { icon: '💔', num: '40% Increase', text: 'In anxiety from phone overuse', color: 'text-pink-400' },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-3xl p-8 text-center hover:border-white/20 hover:-translate-y-1 transition-all">
                <span className="text-5xl mb-2 block">{item.icon}</span>
                <div className={`text-3xl font-extrabold ${item.color} mb-2`}>{item.num}</div>
                <div className="text-gray-400 text-sm leading-relaxed">{item.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SCIENCE ==================== */}
      <section className="px-4 md:px-8 py-24 relative z-10 bg-gradient-to-b from-indigo-500/5 to-transparent" id="science">
        <div className="max-w-6xl mx-auto">
          <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4">🧪 The Science</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-12 leading-tight">How to Avoid Distractions: Why Deleting Apps Doesn't Work</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: '🧠', title: 'The Dopamine Trap', body: "Social media triggers dopamine hits - the same chemical released when eating or winning. Your brain becomes dependent on these quick rewards, making it harder to focus on meaningful work." },
              { icon: '❌', title: 'Why Cold Turkey Fails', body: "Deleting apps creates intense cravings. Your brain, deprived of dopamine, pushes you to reinstall. Studies show 80% of people who delete social media reinstall within a week." },
              { icon: '✅', title: 'The FocusDen Approach', body: "Gradual reduction works better than elimination. FocusDen blocks apps during specific times, allowing your brain to adjust slowly. You build discipline without the withdrawal symptoms." },
            ].map((c, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-3xl p-8">
                <div className="text-4xl mb-4">{c.icon}</div>
                <h3 className="text-xl font-bold mb-3">{c.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm">{c.body}</p>
              </div>
            ))}
          </div>
          <div className="text-center bg-indigo-500/5 border border-indigo-500/10 rounded-3xl p-8 md:p-12">
            <span className="text-6xl opacity-30">"</span>
            <p className="text-xl italic text-gray-300 max-w-3xl mx-auto leading-relaxed mb-4">
              The key isn't to eliminate distractions entirely, but to <strong>control when</strong> you engage with them. This preserves willpower and builds sustainable habits.
            </p>
            <span className="text-sm text-gray-500 block">— Behavioral Psychology Research</span>
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="px-4 md:px-8 py-24 relative z-10" id="how-it-works">
        <div className="max-w-6xl mx-auto">
          <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4">🚀 How It Works</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-16 leading-tight text-center md:text-left">Start Focusing in 3 Simple Steps</h2>
          <div className="space-y-6">
            {[
              { n:'01', title:'Create a Focus Task', desc:"Tap \"Add Task\" and give it a name like \"Study Time\" or \"Work Focus\". Set your start time, end time, and which days to repeat.", emoji:'📋 → ⏰ → 📅' },
              { n:'02', title:'Select Distracting Apps', desc:'Choose which apps to block during this task - Instagram, TikTok, YouTube, games, or any app that steals your focus. Different tasks can block different apps.', emoji:'📱 → 🚫 → ✅' },
              { n:'03', title:'Stay Focused Automatically', desc:"FocusDen automatically blocks your selected apps during the scheduled time. If you try to open a blocked app, you'll see a motivational blocker screen instead.", emoji:'🎯 → 🔒 → 🏆' },
            ].map((step, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="w-0.5 h-10 bg-gradient-to-b from-indigo-500/50 to-transparent mx-auto"></div>}
                <div className="grid grid-cols-1 md:grid-cols-[80px_1fr_auto] gap-4 md:gap-8 items-center bg-white/5 border border-white/10 rounded-3xl p-6 md:p-8 text-center md:text-left">
                  <div className="text-5xl font-extrabold bg-gradient-to-r from-indigo-500 to-pink-500 bg-clip-text text-transparent mx-auto md:mx-0">{step.n}</div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                    <p className="text-gray-400 leading-relaxed text-sm">{step.desc}</p>
                  </div>
                  <div className="text-3xl tracking-widest opacity-60 hidden md:block">{step.emoji}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FEATURES ==================== */}
      <section className="px-4 md:px-8 py-24 relative z-10 bg-gradient-to-b from-transparent to-indigo-500/5" id="features">
        <div className="max-w-6xl mx-auto">
          <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4">✨ Features</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-tight">Everything You Need to Stay Focused</h2>
          <p className="text-lg text-gray-400 max-w-2xl mb-12 leading-relaxed">Powerful tools designed to help you build better digital habits</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <div key={i}
                className={`bg-white/5 border-2 rounded-3xl p-7 transition-all duration-300 cursor-pointer ${activeFeature === i ? `border-${f.color}-500 -translate-y-2` : 'border-white/10 hover:border-white/20'}`}
                onMouseEnter={() => setActiveFeature(i)}>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-${f.color}-500/15 text-${f.color}-400`}>
                  <span className="text-3xl">{f.icon}</span>
                </div>
                <h3 className="text-lg font-bold mb-2">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== STRICT MODE ==================== */}
      <section className="px-4 md:px-8 py-24 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4">🔒 Premium Protection</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-12 leading-tight">For When Willpower Isn't Enough</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
              <div className="flex items-center gap-4 mb-4"><span className="text-5xl">🛡️</span><h3 className="text-2xl font-bold">Strict Mode</h3></div>
              <p className="text-gray-400 mb-6 leading-relaxed">Make it impossible to disable blocking during focus time. Choose your challenge:</p>
              <div className="space-y-3">
                {[['🔢','PIN Code - Set a complex PIN you\'ll forget'],['⏳','Time Delay - Wait 5-30 minutes to disable'],['🧮','Math Challenge - Solve problems to unlock'],['⌨️','Typing Challenge - Type a long phrase correctly']].map(([icon,text],i)=>(
                  <div key={i} className="flex items-center gap-3 bg-indigo-500/5 px-4 py-3 rounded-xl text-sm"><span>{icon}</span><span>{text}</span></div>
                ))}
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
              <div className="flex items-center gap-4 mb-4"><span className="text-5xl">🚫</span><h3 className="text-2xl font-bold">Uninstall Protection</h3></div>
              <p className="text-gray-400 mb-6 leading-relaxed">When Strict Mode is on, FocusDen can't be easily uninstalled. This prevents impulsive removal when your willpower is at its lowest.</p>
              <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 px-4 py-3 rounded-xl text-sm text-amber-200">
                <span>💡</span><span>Uses Android's Device Admin feature. You must complete the strict mode challenge first to uninstall.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <section className="px-4 md:px-8 py-24 relative z-10 bg-gradient-to-b from-indigo-500/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4">💬 Reviews</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-12 leading-tight">Loved by 10k+ Users</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name:'Priya S.',  role:'Medical Student, AIIMS',    text:"FocusDen helped me crack NEET. The strict mode with math challenges is genius - by the time I solve them, the urge to scroll is gone!", avatar:'👩‍⚕️' },
              { name:'Rahul M.',  role:'Software Developer',         text:"Finally shipped my side project. I schedule 'Deep Work' blocks with all social media blocked. The analytics show I've saved 3+ hours daily.", avatar:'👨‍💻' },
              { name:'Ananya K.', role:'Content Creator',            text:"The task-based approach is perfect. I block distractions while editing but can use Instagram for posting later. Flexible yet effective!", avatar:'🎬' },
            ].map((t,i)=>(
              <div key={i} className="bg-white/5 border border-white/10 rounded-3xl p-8">
                <div className="text-amber-500 tracking-widest mb-4 text-sm">★★★★★</div>
                <p className="text-base leading-relaxed mb-6 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl bg-indigo-500/10 w-12 h-12 rounded-full flex items-center justify-center">{t.avatar}</span>
                  <div><div className="font-semibold">{t.name}</div><div className="text-sm text-gray-500">{t.role}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== PRICING ==================== */}
      <section className="px-4 md:px-8 py-24 relative z-10" id="pricing">
        <div className="max-w-6xl mx-auto">
          <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4 animate-slide-up">💎 Pricing</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-tight animate-slide-up">Choose Your Plan</h2>
          <p className="text-lg text-gray-400 mb-8 leading-relaxed animate-slide-up">Start free, upgrade when you need more power</p>

          <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-2xl p-6 mb-12 animate-slide-up">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { val:1247,  color:'text-indigo-400', label:'Joined This Week' },
                { val:89, suffix:'%', color:'text-green-400', label:'Upgrade to Premium' },
                { val:3.2, dec:1, suffix:'hr', color:'text-amber-400', label:'Avg. Time Saved Daily' },
                { val:40, suffix:'% OFF', color:'text-pink-400', label:'Limited Time Only' },
              ].map((s,i)=>(
                <div key={i}>
                  <div className={`text-2xl font-bold ${s.color}`}><AnimatedCounter end={s.val} suffix={s.suffix||''} decimals={s.dec||0}/></div>
                  <div className="text-xs text-gray-400">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free */}
            <div className={`bg-white/5 border-2 rounded-[28px] p-10 relative transition-all duration-500 cursor-pointer ${selectedPlan==='free'?'border-indigo-500/50 shadow-xl shadow-indigo-500/20 scale-105':'border-white/10 hover:border-white/20'}`} onClick={()=>setSelectedPlan('free')} onMouseEnter={()=>setHoveredFeature('free')} onMouseLeave={()=>setHoveredFeature(null)}>
              {selectedPlan==='free' && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-500 px-4 py-1.5 rounded-full text-xs font-bold animate-scale-in">✓ SELECTED</div>}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold">Free</h3>
                {hoveredFeature==='free' && <span className="text-xs bg-green-500/20 text-green-400 px-3 py-1 rounded-full animate-scale-in">Perfect for beginners</span>}
              </div>
              <div className="mb-6"><span className="text-5xl font-extrabold">₹0</span><span className="text-gray-500"> forever</span></div>
              <ul className="space-y-3 mb-8 text-sm text-gray-300">
                {['Maximum 3 tasks','Block 3 apps','Custom schedules','Basic analytics (7 days)','Motivational blocker screen'].map((f,i)=>(
                  <li key={i} className="flex items-start gap-2 transition-all hover:translate-x-1"><span className="text-green-400 mt-0.5">✓</span><span>{f}</span></li>
                ))}
                {['Strict Mode','Super Focus Mode'].map((f,i)=>(
                  <li key={i} className="flex items-start gap-2 opacity-50"><span className="text-gray-600 mt-0.5">✗</span><span>{f}</span></li>
                ))}
              </ul>
              <button onClick={()=>handlePlanClick('free')} className="w-full py-4 rounded-2xl border border-white/10 bg-transparent text-white text-base font-semibold hover:bg-white/5 transition-all relative overflow-hidden group">
                <span className="relative z-10">Get Started Free</span>
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/0 via-indigo-500/10 to-indigo-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              </button>
              {selectedPlan==='free' && <div className="mt-4 text-xs text-center text-gray-400 animate-slide-up">Perfect! Download the app to get started →</div>}
            </div>

            {/* Premium */}
            <div className={`bg-gradient-to-b from-indigo-500/10 to-purple-600/5 border-2 rounded-[28px] p-10 relative transition-all duration-500 cursor-pointer ${selectedPlan==='premium'?'border-indigo-500 shadow-2xl shadow-indigo-500/30 scale-105':'border-indigo-500/30 hover:border-indigo-500/50'}`} onClick={()=>setSelectedPlan('premium')} onMouseEnter={()=>{setHoveredFeature('premium');setShowPricingTooltip(true);}} onMouseLeave={()=>{setHoveredFeature(null);setShowPricingTooltip(false);}}>
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-red-500 px-5 py-1.5 rounded-full text-xs font-bold animate-pulse">⭐ BEST VALUE</div>
              {selectedPlan==='premium' && <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-scale-in">✓ SELECTED</div>}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold">Premium</h3>
                {hoveredFeature==='premium' && <span className="text-xs bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full animate-scale-in">Most popular choice</span>}
              </div>
              <div className="mb-6 relative">
                <div className="flex items-baseline gap-2">
                  <span className="line-through text-gray-500 text-lg">₹999</span>
                  <span className="text-5xl font-extrabold">₹590</span>
                  <span className="text-gray-500">/year</span>
                </div>
                {showPricingTooltip && <div className="absolute -bottom-8 left-0 bg-amber-500/20 border border-amber-500/30 px-3 py-1.5 rounded-lg text-xs text-amber-300 animate-slide-up whitespace-nowrap">💰 You save ₹409 (41% OFF)</div>}
              </div>
              <ul className="space-y-3 mb-8 text-sm text-gray-300">
                {[['✓','Everything in Free'],['⭐','Notification blocking','font-bold'],['⭐','Strict Mode (PIN, Math, Delay)','font-bold'],['⭐','Uninstall protection','font-bold'],['⭐','Super Focus Mode','font-bold'],['✓','Ambient sounds (Rain, Lo-fi)'],['✓','Full analytics history'],['✓','No ads forever']].map(([icon,text,bold],i)=>(
                  <li key={i} className="flex items-start gap-2 transition-all hover:translate-x-1">
                    <span className={`mt-0.5 ${icon==='⭐'?'text-amber-400':'text-green-400'}`}>{icon}</span>
                    <span className={bold}>{bold?<strong>{text}</strong>:text}</span>
                  </li>
                ))}
              </ul>
              <button onClick={()=>handlePlanClick('premium')} className="w-full py-4 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-base font-semibold shadow-lg shadow-indigo-500/30 hover:scale-105 transition-all relative overflow-hidden group">
                <span className="relative z-10">Upgrade to Premium</span>
                <div className="absolute inset-0 animate-shimmer"></div>
              </button>
              {selectedPlan==='premium' && (
                <div className="mt-4 space-y-2 animate-slide-up">
                  <div className="text-xs text-center text-gray-400">✨ Unlock full features in the app after download</div>
                  <div className="flex items-center justify-center gap-2 text-xs text-green-400"><span>🔒</span><span>7-day money-back guarantee</span></div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 px-6 py-3 rounded-full text-sm">
              <span className="text-amber-500">⭐⭐⭐⭐⭐</span>
              <span className="text-gray-400">Rated 4.8/5 by 3,200+ users</span>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section className="px-4 md:px-8 py-24 relative z-10 bg-gradient-to-b from-indigo-500/5 to-transparent" id="faq">
        <div className="max-w-4xl mx-auto">
          <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4">❓ FAQ</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-8 leading-tight">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((faq,i)=>(
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden cursor-pointer hover:border-white/20 transition-colors" onClick={()=>setOpenFaq(openFaq===i?null:i)}>
                <div className="flex justify-between items-center px-6 py-5 font-semibold text-base">
                  <span>{faq.q}</span>
                  <span className="transition-transform duration-300 text-sm" style={{transform:openFaq===i?'rotate(180deg)':'rotate(0)'}}>▼</span>
                </div>
                {openFaq===i && <div className="px-6 pb-5 text-gray-400 leading-relaxed text-sm animate-slide-up">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== DOWNLOAD CTA ==================== */}
      <section className="px-4 md:px-8 py-32 text-center relative z-10 bg-[radial-gradient(ellipse_at_center,_rgba(99,102,241,0.15)_0%,_transparent_70%)]" id="download">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 leading-tight">
            Ready to Take Control<br className="hidden sm:block"/>of Your Screen Time?
          </h2>
          <p className="text-xl text-gray-400 mb-10 max-w-lg mx-auto leading-relaxed">Join 10000+ people who've already transformed their digital habits with FocusDen.</p>
          <a href="https://play.google.com/store/apps/details?id=com.supnistic_ai.distraX"
            className="inline-flex items-center gap-4 bg-white text-[#0A0A14] px-10 py-5 rounded-3xl text-lg shadow-[0_20px_50px_rgba(255,255,255,0.1)] hover:scale-105 transition-transform mb-8"
            aria-label="Download FocusDen from Google Play Store">
            <span className="text-4xl">▶</span>
            <div className="text-left">
              <div className="text-xs opacity-80 uppercase">Get it on</div>
              <div className="text-2xl font-bold">Google Play</div>
            </div>
          </a>
          <div className="flex flex-wrap justify-center gap-6 text-gray-500 text-sm">
            <span>🔒 100% Private</span><span>📵 No Data Collection</span><span>⚡ Lightweight (40MB)</span><span>🆓 Free to Start</span>
          </div>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="border-t border-white/5 px-4 md:px-8 py-16 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-3xl">🎯</span>
                <span className="text-2xl font-extrabold bg-gradient-to-r from-indigo-500 to-pink-500 bg-clip-text text-transparent">FocusDen</span>
              </div>
              <p className="text-gray-500 max-w-xs leading-relaxed text-sm">
                Built by <strong className="text-gray-400">Smit Nikumbh</strong> under Supnistiq Labs.<br/>
                Your digital wellbeing companion.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-12 md:gap-16">
              <div>
                <h4 className="font-semibold mb-4">Product</h4>
                <div className="flex flex-col gap-3">
                  <a href="#features" className="text-gray-500 hover:text-white transition-colors text-sm">Features</a>
                  <a href="#pricing"  className="text-gray-500 hover:text-white transition-colors text-sm">Pricing</a>
                  <a href="#download" className="text-gray-500 hover:text-white transition-colors text-sm">Download</a>
                  <a href="#faq"      className="text-gray-500 hover:text-white transition-colors text-sm">FAQ</a>
                  {/* ← NEW */}
                  <Link to="/about"  className="text-gray-500 hover:text-white transition-colors text-sm">About Founder</Link>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-4">Legal</h4>
                <div className="flex flex-col gap-3">
                  <Link to="/privacy" className="text-gray-500 hover:text-white text-sm">Privacy Policy</Link>
                  <a href="/terms"    className="text-gray-500 hover:text-white transition-colors text-sm">Terms of Service</a>
                  <a href="mailto:official.supnistic.ai@gmail.com" className="text-gray-500 hover:text-white transition-colors text-sm">Contact Us</a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-white/5 pt-8 text-center text-gray-500 text-sm">
            <p className="mb-2">© 2026 FocusDen by <strong className="text-gray-400">Smit Nikumbh</strong>. All rights reserved. Made with ❤️ in Pune, India</p>
            <p className="text-xs text-gray-600">
              App Blocker • Block Distracting Apps • Focus App • Digital Wellbeing • Screen Time Control • Stay Focused • Distraction Blocker • Smit Nikumbh • Supnistiq Labs • Best App Blocker for Students
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}